// combatpro/panels/monsters/MonsterTabs.js
//
// Combat PRO - MonsterTabs (Stage 40: Monster Panel Integration).
//
// Stage 31 left this as 16 hardcoded static tab buttons. Stage 40 wires them to
// Monster Runtime state: the tabs are now rendered from state.monsters, the
// active tab reflects state.currentCombatantIndex, clicking a tab selects that
// monster via the existing setActiveMonster handler (Stage 38), the tab name is
// derived via monsterEngine.deriveTabName (Stage 38), and the initiative badge
// shows the monster's runtime initiative (updateInitiativeBadges, legacy 5452:
// initiative || 0). Markup, class names, and structure are otherwise unchanged.
//
// The active class matches the static baseline (only 'active' was used there);
// the HP-tint tab classes (updateTabColor) and the vi-active/inactive-combatant
// flow classes are NOT in this stage's approved scope and are deferred. No DOM
// manipulation, no getElementById. No optional chaining. No em dash.
//
// Stage 48B-2 (Manual Monster Target Selection): tabs now participate in manual
// spell targeting. While manual mode is active for some (caster, spell), clicking
// a tab toggles that monster as a target via the context handleManualTargetClick
// handler instead of selecting it; outside manual mode the handler returns false
// and the tab falls through to setActiveMonster, exactly as before. Tabs whose
// index is in the active mode's target list get the legacy .vi-target-highlight
// class, derived from state (replacing the legacy getElementById('tab-N') /
// classList in vi_refreshTargetHighlights, 12003-12016). Monster targets only.

import React, { useContext } from 'react';
import { CombatContext } from '../../contexts/CombatContext';
import { deriveFullName } from '../../monsters/monsterEngine';

function MonsterTabs() {
  const { state, setActiveMonster, handleManualTargetClick, spellTargetKey } = useContext(CombatContext);
  const monsters = state.monsters;
  const activeIndex = state.currentCombatantIndex;
  // Only the active slots are shown (legacy activeCombatants count); selecting a
  // lower Active Slots value hides the monster tabs beyond it.
  const activeCount = Math.min(state.activeMonsterCount || monsters.length, monsters.length);

  // Stage 48B-2: derive the highlight set from the ACTIVE manual mode's target
  // list only (legacy vi_refreshTargetHighlights keyed off vi_manualSelectMode,
  // 12006-12009). Monster indices only - the legacy filter at 12011 skipped "p:"
  // player ids, which are not migrated in this stage.
  const mode = state.manualSelectMode;
  let highlightTargets = [];
  if (mode) {
    const highlightKey = spellTargetKey(mode.casterIndex, mode.spellIdx);
    highlightTargets = state.spellTargets[highlightKey] || [];
  }

  function isHighlighted(index) {
    return highlightTargets.some(t => String(t) === String(index));
  }

  // Stage 48B-2: legacy vi_handleManualTargetClick returns true when it consumes
  // the click (manual mode active), in which case the tab does NOT also select.
  function onTabClick(index) {
    if (!handleManualTargetClick(index)) {
      setActiveMonster(index);
    }
  }

  return (
    <div className="monster-tabs" id="monsterTabs">
      {monsters.slice(0, activeCount).map((monster, index) => {
        let className = index === activeIndex ? 'tab active' : 'tab';
        if (isHighlighted(index)) {
          className = className + ' vi-target-highlight';
        }
        return (
          <button
            key={index}
            className={className}
            onClick={() => onTabClick(index)}
          >
            <span id={'tabName-' + index}>{deriveFullName(monster, index)}</span>
            <span className="tab-initiative" id={'tabInit-' + index}>{monster.initiative || 0}</span>
          </button>
        );
      })}
    </div>
  );
}

export default MonsterTabs;
